import { message, Space, Alert, Select } from "antd";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Company } from "@rumble-it/common-lib/types/models/Company.interface";
import { DocumentLibraryItem } from "@rumble-it/common-lib/types/models/DocumentLibraryItem.interface";
import {
  linkCompanyToDocument,
  unlinkCompanyFromDocument,
} from "../../../../lib/api/resources/documentLibraries";
import { fetchCompanies } from "../../../../lib/api/resources/companies";
import { Modal } from "@/components/v2";
import { Button, Card, CardFooter } from "@rumbledirect/ui";

interface EditDocumentCompanyModalProps {
  libraryId: string;
  document: DocumentLibraryItem | null;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const EditDocumentCompanyModal = ({
  libraryId,
  document,
  open,
  setOpen,
}: EditDocumentCompanyModalProps) => {
  const [companyIds, setCompanyIds] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const currentCompanies = document?.companies ?? [];

  useEffect(() => {
    setCompanyIds(document?.companies?.map((company) => company.id) ?? []);
  }, [document]);

  const companiesQuery = useQuery({
    queryKey: ["companies"],
    queryFn: () => fetchCompanies({ pagination: false }),
    enabled: open,
    select: (data) =>
      data.member.map((company: Company) => ({
        label: company.fullName,
        value: company.id,
      })),
  });

  const updateCompanyMutation = useMutation({
    mutationFn: async () => {
      if (!document) throw new Error("No document provided");

      const companiesToUnlink = currentCompanies.filter(
        (company) => !companyIds.includes(company.id),
      );

      const companyIdsToLink = companyIds.filter(
        (id) => !currentCompanies.some((company) => company.id === id),
      );

      await Promise.all([
        ...companiesToUnlink.map((company) =>
          unlinkCompanyFromDocument(libraryId, document.id, company.id),
        ),
        ...companyIdsToLink.map((id) =>
          linkCompanyToDocument(libraryId, document.id, id),
        ),
      ]);
    },
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["documentLibraryItems"],
      });
      message.success("Bedrijven succesvol bijgewerkt");
      setOpen(false);
    },
    onError: () =>
      message.error(
        "Er is een fout opgetreden bij het bijwerken van de bedrijven",
      ),
  });

  return (
    <Modal open={open} onCancel={() => setOpen(false)}>
      <Card title="Bedrijven bijwerken">
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Alert
            type="info"
            showIcon
            message="Selecteer de bedrijven die aan dit document zijn gekoppeld."
          />
          <Select
            allowClear
            showSearch
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Selecteer een of meer bedrijven (optioneel)"
            options={companiesQuery.data}
            loading={companiesQuery.isLoading}
            value={companyIds}
            onChange={(value) => setCompanyIds(value)}
            optionFilterProp="label"
            disabled={updateCompanyMutation.isPending}
          />
        </Space>
        <CardFooter>
          <Button
            type="primary"
            onClick={() => updateCompanyMutation.mutate()}
            loading={updateCompanyMutation.isPending}
          >
            Opslaan
          </Button>
        </CardFooter>
      </Card>
    </Modal>
  );
};
